import React, { Fragment, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Divider,
  Drawer,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { DashboardTable } from "./dashboard-table";
import { ErrorLogsTable } from "./error-logs-table";
import { Link, NavLink } from "react-router-dom";
import { DEFAULT_LOGO_URL } from "../../constants";
import { OutlinedButton } from "../../styled/outlined-button";

import LogoutIcon from "@mui/icons-material/Logout";
import { useSignOut } from "../../../hooks/useSignOut";
import { SupervisedUserCircleOutlined } from "@mui/icons-material";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { CreateUserForm } from "./dashboard-create-user";
import {
  backupData,
  deleteAdminUser,
  downloadLatestBackup,
  getAdminUsers,
  getErrorLogs,
  INewUserAdmin,
  lastUpdateBackup,
  setNewUser,
  updateAdminUser,
} from "../../../api/admin";
import { IAdminUser } from "../../../interface/IAdminUser";
import useSnackbar from "../../../hooks/useSnackbar";
import { DashboardBackup } from "./dashboard-backup-table";
import { IAdminLog } from "../../../interface/IAdminLog";

export const Dashboard: React.FC = () => {
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showBackupUser, setShowBackupUser] = useState(false);
  const [showErrorList, setShowErrorList] = useState(false);
  const [showTableUser, setShowTableUser] = useState(true);
  const [adminUser, setAdminUser] = useState<IAdminUser>();
  const [errorLogs, setErrorLogs] = useState<IAdminLog["logs"]>();
  const [userAdminId, setUserAdminId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [status, setStatus] = useState(false);

  const { message, severity, open, showSnackbar, closeSnackbar } =
    useSnackbar();

  const theme = useTheme();
  const { signOut } = useSignOut();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleGetUserId = (userId: string) => {
    setShowCreateUser(true);
    setUserAdminId(userId);
  };

  //event handler showCreateUser
  const handleShowCreateUserClick = () => {
    setShowCreateUser(true);
    setShowBackupUser(false);
    setShowTableUser(false);
    setUserAdminId("");
  };

  //event handler showErrorList
  const handleErrorLists = () => {
    setShowCreateUser(false);
    setShowBackupUser(false);
    setShowTableUser(false);
    setShowErrorList(true);
  };

  const handleShowbackUpClick = () => {
    setShowBackupUser(true);
    setShowCreateUser(false);
    setShowTableUser(false);
  };

  //fetching data from the server
  const getAdminUsersData = async () => {
    try {
      setLoading(true);
      const response = await getAdminUsers();
      setAdminUser(response.data);
    } catch (error) {
      console.error("Error fetching users: ", error);
    } finally {
      setLoading(false);
    }
  };

  //fetching error logs
  const getErrorLogsData = async () => {
    try {
      setLoading(true);
      const response = await getErrorLogs();
      setErrorLogs(response?.data?.logs);
    } catch (error) {
      console.error("Error fetching error logs: ", error);
    } finally {
      setLoading(false);
    }
  };

  //Create new user
  const handleFormSubmit = async (userData: INewUserAdmin) => {
    try {
      setLoading(true);
      const newUser = await setNewUser(userData);

      if (newUser) {
        await getAdminUsersData();
        setShowTableUser(true);
        setShowCreateUser(false);
        showSnackbar("Benutzer erfolgreich erstellt", "success");
      }
    } catch (error: any) {
      console.error(error.response.data.message);
      showSnackbar(error.response.data.message, "error");
    } finally {
      setLoading(false);
    }
  };

  //Update user
  const handleUpdateUser = async (
    userAdminId: string,
    userData: INewUserAdmin
  ) => {
    try {
      setLoading(true);
      const updateUser = await updateAdminUser(userAdminId, userData);
      if (updateUser) {
        await getAdminUsersData();
        showSnackbar("Benutzer erfolgreich aktualisiert", "success");
      }
    } catch (error: any) {
      console.error(error.response.data);
      showSnackbar(error.response.data.message, "error");
    } finally {
      setLoading(false);
    }
  };

  //delete user
  const handleDeleteUserId = async (userId: string) => {
    try {
      const deleteUser = await deleteAdminUser(userId);
      if (deleteUser) {
        await getAdminUsersData();
        showSnackbar("Benutzer erfolgreich gelöscht", "success");
      }
    } catch (error: any) {
      console.error(error.response.data);
      showSnackbar(error.response.data.message, "error");
    }
  };

  // backup last update
  const handlerLastBackup = async () => {
    try {
      const lastBacukup = await lastUpdateBackup();

      setStatus(true);

      const dateParse = new Date(lastBacukup.data?.lastBackup).toDateString();
      const hourParse = new Date(
        lastBacukup.data?.lastBackup
      ).toLocaleTimeString();
      setDate(dateParse + " " + hourParse);
    } catch (error) {
      console.log(error);
    }
  };

  //backup data
  const handleBackup = async () => {
    try {
      setLoading(true);
      const response = await backupData();
      if (response.status === 200) {
        handlerLastBackup();
      }
    } catch (error: any) {
      console.error(error);
      showSnackbar(
        error.response?.data?.message ?? "Backup fehlgeschlagen",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  //download latest backup (the link expires after 15 minutes)
  const handleDownloadBackup = async () => {
    try {
      const response = await downloadLatestBackup();
      window.location.href = response.data.url;
    } catch (error: any) {
      console.error(error);
      showSnackbar(
        error.response?.data?.message ?? "Download fehlgeschlagen",
        "error"
      );
    }
  };

  useEffect(() => {
    getAdminUsersData();
    getErrorLogsData();
    handlerLastBackup();
  }, []);

  return (
    <Box style={{ display: "flex" }}>
      <Drawer sx={{ width: "30%", flexShrink: 0 }} variant="permanent">
        <Grid container justifyContent="center" width="250px" p={2}>
          <Box>
            <Link to="/">
              <img
                src={DEFAULT_LOGO_URL}
                alt="Clinic Logo"
                style={{ height: "3em" }}
              />
            </Link>
          </Box>

          <Typography variant="h6">Benutzerverwaltung</Typography>
        </Grid>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Divider
            sx={{
              width: "80%",
              borderColor: "black",
              borderBottomWidth: "1px",
              backgroundColor: "black",
            }}
          />
        </Box>

        <List>
          <ListItem>
            <ListItemButton
              onClick={() => {
                setShowCreateUser(false);
                setShowTableUser(true);
              }}
            >
              <ListItemIcon>
                <ListAltRoundedIcon />
              </ListItemIcon>
              <ListItemText primary="Panel" sx={{ ml: -2 }} />
            </ListItemButton>
          </ListItem>

          <ListItem>
            <ListItemButton onClick={handleShowCreateUserClick}>
              <ListItemIcon>
                <SupervisedUserCircleOutlined />
              </ListItemIcon>
              <ListItemText primary="Benutzer Anlegen" sx={{ ml: -2 }} />
            </ListItemButton>
          </ListItem>

          <ListItem>
            <ListItemButton onClick={handleShowbackUpClick}>
              <ListItemIcon>
                <CloudUploadIcon />
              </ListItemIcon>
              <ListItemText primary="Ein Backup erstellen" sx={{ ml: -2 }} />
            </ListItemButton>
          </ListItem>

          <ListItem>
            <ListItemButton onClick={handleErrorLists}>
              <ListItemIcon>
                <CloudUploadIcon />
              </ListItemIcon>
              <ListItemText
                primary="Liste von Synchronisationsproblemen"
                sx={{ ml: -2 }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Grid
          container
          justifyContent="center"
          alignItems="end"
          height="100vh"
          p={2}
        >
          <OutlinedButton
            color="inherit"
            size={isMobile ? "small" : "medium"}
            startIcon={<LogoutIcon />}
            onClick={signOut}
          >
            Abmelden
          </OutlinedButton>
        </Grid>
      </Drawer>

      <Fragment>
        <Box sx={{ flexGrow: 1, padding: "20px" }}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            mt="12px"
          >
            <NavLink
              to="/"
              style={({ isActive }) => ({
                color: "black",
                fontWeight: isActive ? "700" : "normal",
                textDecoration: isActive ? "underline" : "none",
                marginInline: 10,
              })}
            >
              Home
            </NavLink>
            {"|"}
            <NavLink
              to="/patients"
              style={({ isActive }) => ({
                color: "black",
                fontWeight: isActive ? "700" : "normal",
                textDecoration: isActive ? "underline" : "none",
                marginInline: 10,
              })}
            >
              Patientenliste
            </NavLink>

            {"|"}
            <NavLink
              to="/admin"
              style={({ isActive }) => ({
                color: "black",
                fontWeight: isActive ? "700" : "normal",
                textDecoration: isActive ? "underline" : "none",
                marginInline: 10,
              })}
            >
              Admin-Panel
            </NavLink>
          </Box>
          <Toolbar sx={{ minHeight: "50px" }} />

          {showTableUser && (
            <Fragment>
              <Typography variant="h6" gutterBottom>
                Benutzerliste
              </Typography>
              <Paper elevation={3}>
                <DashboardTable
                  user={adminUser?.user || {}}
                  getUserId={handleGetUserId}
                  getDeleteUserId={handleDeleteUserId}
                />
              </Paper>
            </Fragment>
          )}

          <Toolbar sx={{ minHeight: "50px" }} />
          {showCreateUser && (
            <CreateUserForm
              users={adminUser?.user || {}}
              onSubmit={handleFormSubmit}
              onSumitUpdate={handleUpdateUser}
              showForm={setShowCreateUser}
              userId={userAdminId}
              setUserId={setUserAdminId}
            />
          )}
          {showBackupUser && (
            <DashboardBackup
              date={date}
              handleBackupAction={handleBackup}
              handleDownloadAction={handleDownloadBackup}
              status={status}
              loading={loading}
            />
          )}
          {showErrorList && (
            <Fragment>
              <Typography variant="h6" gutterBottom>
                Liste von Synchronisationsproblemen
              </Typography>
              <Paper elevation={3}>
                <ErrorLogsTable
                  logs={errorLogs || []}
                  getUserId={handleGetUserId}
                  getDeleteUserId={handleDeleteUserId}
                />
              </Paper>
            </Fragment>
          )}
        </Box>
      </Fragment>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={severity} variant="filled" onClose={closeSnackbar}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
